import {
  Clear,
  Close,
  ExpandLess,
  ExpandMore,
  FilterList,
  Search,
} from "@mui/icons-material";
import {
  Box,
  Collapse,
  Drawer,
  Grid2,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Paper,
} from "@mui/material";
import { KeyboardEvent, ReactNode, useState } from "react";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { swayyColors, swayyFont, swayyRadii } from "../../../theme/tokens";

const FILTER_FIELD_HEIGHT = 42;

export const SWAYY_FILTER_DROPDOWN_DEFAULTS = {
  local: true as const,
  fontSize: "12px",
  inputHeight: `${FILTER_FIELD_HEIGHT}px`,
  borderRadius: swayyRadii.sm,
  borderColor: swayyColors.border,
};

const searchInputSx = {
  borderRadius: swayyRadii.sm,
  fontFamily: swayyFont,
  fontSize: 13,
  height: FILTER_FIELD_HEIGHT,
  width: "100%",
  bgcolor: swayyColors.white,
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: swayyColors.border,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: swayyColors.pine,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: swayyColors.pine,
  },
};

const dateFieldSx = {
  width: "100%",
  "& .MuiInputBase-root": {
    height: `${FILTER_FIELD_HEIGHT}px !important`,
    minHeight: `${FILTER_FIELD_HEIGHT}px !important`,
    boxSizing: "border-box",
    borderRadius: `${swayyRadii.sm} !important`,
    borderColor: `${swayyColors.border} !important`,
    fontFamily: swayyFont,
    bgcolor: swayyColors.white,
    padding: "0 10px !important",
    "&:hover": {
      borderColor: `${swayyColors.pine} !important`,
    },
  },
};

const dropdownWrapSx = {
  width: "100%",
  "& > div > div:first-of-type": {
    height: `${FILTER_FIELD_HEIGHT}px !important`,
    minHeight: `${FILTER_FIELD_HEIGHT}px !important`,
    maxHeight: `${FILTER_FIELD_HEIGHT}px !important`,
    paddingTop: "0 !important",
    paddingBottom: "0 !important",
    boxSizing: "border-box",
    borderRadius: `${swayyRadii.sm} !important`,
    borderColor: `${swayyColors.border} !important`,
    "&:hover": {
      borderColor: `${swayyColors.pine} !important`,
    },
  },
};

const clearButtonSx = {
  minWidth: { xs: "100%", sm: 140 },
  height: 40,
  minHeight: 40,
  borderRadius: swayyRadii.md,
  bgcolor: "transparent",
  color: swayyColors.deepGreen,
  border: `1px solid ${swayyColors.border}`,
  "&:hover": {
    bgcolor: swayyColors.pineLight,
    border: `1px solid ${swayyColors.pine}`,
  },
};

const applyButtonSx = {
  minWidth: { xs: "100%", sm: 140 },
  height: 40,
  minHeight: 40,
  borderRadius: swayyRadii.md,
  bgcolor: swayyColors.pine,
  color: swayyColors.white,
  "&:hover": { bgcolor: swayyColors.pineHover },
};

interface SwayyFilterSearchInputProps {
  value: string;
  onChange: (value: string) => void;
  onClear: () => void;
  placeholder: string;
  onEnter?: () => void;
  disabled?: boolean;
}

export const SwayyFilterSearchInput = ({
  value,
  onChange,
  onClear,
  placeholder,
  onEnter,
  disabled = false,
}: SwayyFilterSearchInputProps) => {
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter" || !onEnter) return;
    onEnter();
  };

  return (
    <OutlinedInput
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      size="small"
      fullWidth
      disabled={disabled}
      startAdornment={
        <InputAdornment position="start">
          <Search sx={{ fontSize: 18, color: swayyColors.textSecondary }} />
        </InputAdornment>
      }
      endAdornment={
        value ? (
          <InputAdornment position="end">
            <IconButton size="small" onClick={onClear} disabled={disabled}>
              <Clear sx={{ fontSize: 16 }} />
            </IconButton>
          </InputAdornment>
        ) : undefined
      }
      sx={searchInputSx}
    />
  );
};

export const SwayyFilterSearchField = ({
  children,
}: {
  children: ReactNode;
}) => <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>{children}</Grid2>;

export const SwayyFilterDropdownField = ({
  children,
}: {
  children: ReactNode;
}) => (
  <Grid2 size={{ xs: 12, sm: 6, md: 4 }} sx={dropdownWrapSx}>
    {children}
  </Grid2>
);

export const SwayyFilterDateField = ({ children }: { children: ReactNode }) => (
  <Grid2 size={{ xs: 12, sm: 6, md: 4 }} sx={dateFieldSx}>
    {children}
  </Grid2>
);

const FilterBody = ({
  children,
  onClear,
  onApply,
}: {
  children: ReactNode;
  onClear: () => void;
  onApply: () => void;
}) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
    <Grid2 container spacing={1.5} alignItems="center">
      {children}
    </Grid2>
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        flexWrap: "wrap",
        justifyContent: { xs: "stretch", md: "flex-end" },
      }}
    >
      <AppButton
        label="Clear filters"
        onClick={onClear}
        customStyles={clearButtonSx}
      />
      <AppButton
        label="Apply"
        onClick={onApply}
        customStyles={applyButtonSx}
      />
    </Box>
  </Box>
);

interface SwayySearchFiltersPanelProps {
  open: boolean;
  onToggle: () => void;
  onClear: () => void;
  onApply: () => void;
  children: ReactNode;
  title?: string;
  mobileTitle?: string;
}

const SwayySearchFiltersPanel = ({
  open,
  onToggle,
  onClear,
  onApply,
  children,
  title = "Search & Filters",
  mobileTitle = "Filters",
}: SwayySearchFiltersPanelProps) => {
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleApply = () => {
    onApply();
    setMobileOpen(false);
  };

  const handleClear = () => {
    onClear();
    setMobileOpen(false);
  };

  return (
    <>
      <Box sx={{ display: { xs: "block", md: "none" }, mb: 1 }}>
        <AppButton
          label="Filters"
          startIcon={<FilterList />}
          onClick={() => setMobileOpen(true)}
          customStyles={{
            width: "100%",
            height: 40,
            minHeight: 40,
            borderRadius: swayyRadii.md,
            bgcolor: swayyColors.white,
            color: swayyColors.deepGreen,
            border: `1px solid ${swayyColors.border}`,
            "&:hover": { bgcolor: swayyColors.pineLight },
          }}
        />
      </Box>

      <Paper
        elevation={0}
        sx={{
          display: { xs: "none", md: "block" },
          mb: open ? 1.5 : 1,
          borderRadius: swayyRadii.md,
          border: `1px solid ${swayyColors.border}`,
          bgcolor: swayyColors.white,
          overflow: "hidden",
        }}
      >
        <Box
          onClick={onToggle}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            minHeight: open ? 48 : 48,
            px: 1.5,
            py: open ? 0 : 1,
            cursor: "pointer",
            borderBottom: open ? `1px solid ${swayyColors.border}` : "none",
            "&:hover": { backgroundColor: swayyColors.pineLight },
          }}
        >
          <CustomTypography
            variant="heading7"
            color={swayyColors.deepGreen}
            sx={{ fontFamily: swayyFont, fontWeight: 600, lineHeight: 1 }}
          >
            {title}
          </CustomTypography>
          <IconButton
            size="small"
            sx={{
              color: swayyColors.pine,
              p: 0.25,
              "& .MuiSvgIcon-root": { fontSize: 18 },
            }}
            onClick={(event) => {
              event.stopPropagation();
              onToggle();
            }}
          >
            {open ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>

        <Collapse in={open} timeout="auto" unmountOnExit>
          <Box sx={{ p: 2 }}>
            <FilterBody onClear={handleClear} onApply={handleApply}>
              {children}
            </FilterBody>
          </Box>
        </Collapse>
      </Paper>

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 360 },
            p: 2,
            bgcolor: swayyColors.white,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <CustomTypography
            variant="heading6"
            color={swayyColors.deepGreen}
            sx={{ fontFamily: swayyFont, fontWeight: 600 }}
          >
            {mobileTitle}
          </CustomTypography>
          <IconButton
            size="small"
            onClick={() => setMobileOpen(false)}
            sx={{ color: swayyColors.deepGreen }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
        <FilterBody onClear={handleClear} onApply={handleApply}>
          {children}
        </FilterBody>
      </Drawer>
    </>
  );
};

export default SwayySearchFiltersPanel;
